// ============================================================================
// bootstrapOptionsResolvers — wires options_source keys to the master-options API
// ============================================================================
// Called once at app boot. Maps each `RefFieldSpec.options_source` value that
// can appear in a form_spec to a fetcher that calls
// /api/v1/MasterOptions/{masterCode}.
//
// Adding a new master:
//   1. Add a branch in the backend MasterOptionsService.GetAsync().
//   2. Add a `registerOptionsResolver(...)` line here pointing at it.

import { masterOptionsService } from 'api/services/masterOptionsService';
import type { SelectOption } from 'api/types/formSpec.types';
import type { MasterOption } from 'api/types/masterOptions.types';
import { registerOptionsResolver } from './optionsResolver';

const toSelectOption = (m: MasterOption): SelectOption => ({
  value: m.value,
  label: m.label,
  description: m.description ?? undefined,
});

export function bootstrapOptionsResolvers(): void {
  // Yard master — repo event, yard storage check-in.
  registerOptionsResolver('mst.tbl_yard', async () => {
    const rows = await masterOptionsService.get('yard');
    return rows.map(toSelectOption);
  });

  // Valuator master — valuation form. Honors the optional specialization
  // filter (e.g. "VEHICLE,GENERAL" from the form_spec field's options_filter).
  registerOptionsResolver('mst.tbl_valuator', async (params) => {
    // The form_spec uses options_filter strings like
    //   "specialization IN ('VEHICLE','GENERAL')"
    // — extract the IN list for the backend `filter` param.
    const filter = extractSpecialization(params?.filter);
    const rows = await masterOptionsService.get('valuator', filter);
    return rows.map(toSelectOption);
  });

  // User cache filtered to collection-hierarchy users — repo assignment.
  registerOptionsResolver('admin.tbl_user_cache', async () => {
    const rows = await masterOptionsService.get('collection-user');
    return rows.map(toSelectOption);
  });
}

/**
 * Parse a SQL-ish filter expression like
 *   "specialization IN ('VEHICLE','GENERAL')"
 * down to the comma-separated values the backend expects:
 *   "VEHICLE,GENERAL"
 * Returns undefined when no IN clause is found — the backend then returns
 * the unfiltered list.
 */
function extractSpecialization(rawFilter: string | undefined): string | undefined {
  if (!rawFilter) return undefined;
  const match = rawFilter.match(/specialization\s+IN\s*\(([^)]+)\)/i);
  if (!match) return undefined;
  return match[1]
    .split(',')
    .map((s) => s.trim().replace(/^['"]|['"]$/g, ''))
    .join(',');
}
